import { createFileRoute, Link } from '@tanstack/react-router';
import { format, isSameDay, startOfDay } from 'date-fns';
import { Calendar as CalendarIcon, Clock, Plus } from 'lucide-react';
import type { ComponentProps } from 'react';
import { useMemo, useState } from 'react';
import type { DayButton } from 'react-day-picker';
import { useTranslation } from 'react-i18next';
import { AppointmentCard } from '@/components/appointments/appointment-card';
import { AppointmentForm } from '@/components/appointments/appointment-form';
import { AppointmentSkeleton } from '@/components/appointments/appointment-skeleton';
import { ProtectedRoute } from '@/components/auth/protected-route';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { useAppointments, useClaimAppointment, useCreateAppointment } from '@/hooks/use-appointments';
import { useUsers } from '@/hooks/use-users';
import { cn } from '@/lib/utils';
import { useAuth } from '@/providers/auth-provider';
import type { User } from '@/services/api';
import type { CreateAppointmentRequest } from '@/types/appointment';

export const Route = createFileRoute('/')({
  component: () => (
    <ProtectedRoute>
      <HomePage />
    </ProtectedRoute>
  ),
});

function HomePage() {
  const { t } = useTranslation();
  const { session } = useAuth();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [claimingAppointmentId, setClaimingAppointmentId] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(new Date());

  const createAppointmentMutation = useCreateAppointment();
  const claimAppointmentMutation = useClaimAppointment();
  const { data: appointmentsData, isLoading: isLoadingAppointments } = useAppointments();
  const { data: usersData } = useUsers();

  const users: Record<string, User> = usersData?.users || {};
  const allAppointments = appointmentsData?.appointments || [];
  const now = new Date();

  // Calendar-specific data
  const appointmentCounts = useMemo(() => {
    const counts: Record<string, { total: number; unassigned: number }> = {};
    allAppointments.forEach((appointment) => {
      const dateKey = startOfDay(new Date(appointment.startTime)).toISOString();
      if (!counts[dateKey]) counts[dateKey] = { total: 0, unassigned: 0 };
      counts[dateKey].total++;
      if (appointment.status === 'unassigned') counts[dateKey].unassigned++;
    });
    return counts;
  }, [allAppointments]);

  const selectedDateAppointments = useMemo(() => {
    if (!selectedDate) return [];
    return allAppointments
      .filter((appointment) => isSameDay(new Date(appointment.startTime), selectedDate))
      .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
  }, [allAppointments, selectedDate]);

  // Filter appointments for unassigned notification
  const futureUnassignedAppointments = allAppointments
    .filter((appointment) => appointment.status === 'unassigned' && new Date(appointment.startTime) >= now)
    .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());

  const handleCreateAppointment = async (data: CreateAppointmentRequest) => {
    try {
      await createAppointmentMutation.mutateAsync(data);
      setCreateDialogOpen(false);
    } catch (error) {
      console.error('Failed to create appointment:', error);
    }
  };

  const handleClaimAppointment = async (appointmentId: string) => {
    try {
      setClaimingAppointmentId(appointmentId);
      await claimAppointmentMutation.mutateAsync(appointmentId);
    } catch (error) {
      console.error('Failed to claim appointment:', error);
    } finally {
      setClaimingAppointmentId(null);
    }
  };

  const CustomDayButton = ({ day, modifiers, ...props }: ComponentProps<typeof DayButton>) => {
    const dayKey = startOfDay(day.date).toISOString();
    const counts = appointmentCounts[dayKey];

    return (
      <Button
        variant="ghost"
        size="icon"
        className={cn(
          'relative aspect-square size-auto w-full min-w-8 flex-col gap-1 leading-none font-normal',
          modifiers.selected && 'bg-primary text-primary-foreground',
          modifiers.today && 'bg-accent text-accent-foreground font-semibold',
          modifiers.outside && 'text-muted-foreground',
        )}
        {...props}
      >
        <span>{day.date.getDate()}</span>
        {counts && counts.total > 0 && (
          <div className="absolute -top-1 -right-1 flex gap-1">
            <div className="bg-blue-500 text-white text-xs rounded-full min-w-[16px] h-[16px] flex items-center justify-center px-1 text-[10px] leading-none">
              {counts.total}
            </div>
            {counts.unassigned > 0 && (
              <div className="bg-red-500 text-white text-xs rounded-full min-w-[16px] h-[16px] flex items-center justify-center px-1 text-[10px] leading-none">
                !
              </div>
            )}
          </div>
        )}
      </Button>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-end items-center gap-8">
        {futureUnassignedAppointments.length > 0 && (
          <div className="flex-grow">
            <Card className="border-red-200 bg-red-50">
              <CardContent>
                <div className="flex items-center gap-2 text-red-800">
                  <Clock className="h-4 w-4" />
                  <Link to="/needs-driver" className="font-medium hover:underline">
                    {futureUnassignedAppointments.length} {t('appointments.appointmentNeedDrivers')}
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        <div className="flex justify-end items-center">
          <Button onClick={() => setCreateDialogOpen(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            {t('appointments.create')}
          </Button>
        </div>
      </div>

      <div className="grid gap-4 lg:gap-6 lg:grid-cols-[auto_1fr]">
        <Card>
          <CardHeader>
            <CardTitle>{t('calendar.calendarView')}</CardTitle>
          </CardHeader>
          <CardContent>
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={setSelectedDate}
              className="rounded-md border"
              components={{
                DayButton: CustomDayButton,
              }}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{selectedDate ? format(selectedDate, 'MMMM d, yyyy') : t('calendar.selectedDate')}</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoadingAppointments ? (
              <AppointmentSkeleton variant="compact" />
            ) : selectedDateAppointments.length === 0 ? (
              <EmptyState
                icon={CalendarIcon}
                title={t('calendar.noAppointments')}
                description={t('appointments.noAppointmentsScheduledForDate')}
                className="border-0 shadow-none"
              />
            ) : (
              <div className="space-y-3">
                {selectedDateAppointments.map((appointment) => (
                  <AppointmentCard
                    key={appointment.id}
                    appointment={appointment}
                    variant="compact"
                    users={users}
                    currentUserId={session?.user?.id}
                    showCreatedBy={false}
                    actions={
                      appointment.status === 'unassigned' ? (
                        <Button
                          size="sm"
                          onClick={() => handleClaimAppointment(appointment.id)}
                          disabled={claimingAppointmentId === appointment.id}
                          className="mt-2 bg-green-600 hover:bg-green-700 text-white"
                        >
                          {claimingAppointmentId === appointment.id
                            ? t('appointments.claiming')
                            : t('appointments.claim')}
                        </Button>
                      ) : undefined
                    }
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <AppointmentForm
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onSubmit={handleCreateAppointment}
        isLoading={createAppointmentMutation.isPending}
        mode="create"
      />
    </div>
  );
}
